/**
 * SectionHeader — entête de section standardisé du profil Particulier.
 *
 * Compose : eyebrow + titre + description (optionnelle).
 * Variants d'alignement (centré ou aligné à gauche).
 *
 * Utilisé entre les sections d'une page particulier pour garantir un rythme
 * éditorial cohérent (hub, stub parcours, guide d'aide, etc.).
 */

import type { ReactNode } from 'react';
import { cn } from '../../../lib/cn';
import { Reveal } from '../../motion/Reveal';

export interface ParticulierSectionHeaderProps {
  /** Eyebrow uppercase tracking-wide */
  eyebrow: string;
  /** Titre H2 — peut contenir du markup (gradient span, etc.) */
  title: ReactNode;
  /** Description optionnelle (15-40 mots idéaux) */
  description?: ReactNode;
  /** Alignement texte */
  align?: 'left' | 'center';
  /** Largeur max du titre/description (en max-w-prose, max-w-2xl, etc.) */
  maxWidthClass?: string;
  /** Désactive l'animation reveal */
  noReveal?: boolean;
  /** Marge basse (par défaut mb-10) */
  className?: string;
  /** Slot affiché à droite (uniquement quand align=left) */
  rightSlot?: ReactNode;
}

export function ParticulierSectionHeader({
  eyebrow,
  title,
  description,
  align = 'left',
  maxWidthClass,
  noReveal = false,
  className,
  rightSlot,
}: ParticulierSectionHeaderProps) {
  const headerClass = cn(
    align === 'center' ? 'mx-auto text-center' : '',
    maxWidthClass ?? (align === 'center' ? 'max-w-2xl' : 'max-w-3xl'),
  );

  const inner = (
    <div className={headerClass}>
      <p className="eyebrow text-stone-500">{eyebrow}</p>
      <h2
        className={cn(
          'mt-3 font-display font-bold tracking-tight text-ink',
          'text-[clamp(1.75rem,3.5vw,2.5rem)]',
          'text-balance',
        )}
      >
        {title}
      </h2>
      {description ? (
        <p className="mt-4 text-[14.5px] leading-relaxed text-stone-600">{description}</p>
      ) : null}
    </div>
  );

  const content = noReveal ? inner : <Reveal>{inner}</Reveal>;

  // Si rightSlot ET align=left → layout 2 colonnes (flex)
  if (rightSlot && align === 'left') {
    return (
      <div className={cn('flex flex-wrap items-end justify-between gap-6', className)}>
        {content}
        <div>{rightSlot}</div>
      </div>
    );
  }

  return <div className={cn('mb-10', className)}>{content}</div>;
}
